<template>
  <v-container fluid grid-list-xl>
    <v-layout row wrap ma-0 mb-2 pa-0>
      <v-flex pt-0>
        <v-layout>
          <template>
            <h2>
              <span class="grey--text">Tokenize</span> {{ paymentType === 'card' ? 'Card' : 'ACH '}}
            </h2>
            <div class="pa-1 pl-2">
              <div class="subheading primary--text">{{ selectedTerminal.name }}</div>
              <div class="body-2 grey--text">{{ selectedTerminal.code }}</div>
            </div>
            <Lightbulb medium>
              <template v-slot:message>
                <span v-if="domain === 'blueyonder'">Use this page to issue a refund for transactions not originally processed through the BlueYonder terminal. Enter the patient’s {{ paymentType }} information and click 'Tokenize.' Then call customer service at 800-270-9285 to complete the refund.</span>
                <span v-else>This allows {{ paymentType === 'card' ? 'a card' : 'an ACH '}} payment method to be tokenized for future use.</span>
              </template>
            </Lightbulb>
          </template>
        </v-layout>
      </v-flex>
    </v-layout>

    <AccountTerminalDebug :terminal="selectedTerminal"></AccountTerminalDebug>

    <v-form ref="form1" v-model="isFormValid" @submit.prevent>
      <v-layout row wrap>
        <v-flex xs12 sm4>
          <TerminalSelect
            ref="terminal"
            v-model="selectedTerminal"
            :paymentType="paymentType"
            showDetails
          ></TerminalSelect>
        </v-flex>
      </v-layout>

      <v-layout row wrap v-if="paymentType === 'card'">
        <v-flex xs12 sm4>
          <v-text-field
            label="Card Holder Name"
            v-model="fields.card_name"
            append-icon="fa-user"
            :rules="nameRules"
            box
            hide-details
            required
            :error-messages="fields.card_name ? '' : 'Required'"
          ></v-text-field>
        </v-flex>
        <v-flex xs12 sm4>
          <v-text-field
            label="Card Number"
            v-model="fields.card_number"
            :append-icon="(!cardType || cardType==='unknown') ? 'fa-credit-card' : ''"
            :mask="cardMask"
            :rules="cardRules"
            box
            hide-details
            required
            :error-messages="fields.card_number ? '' : 'Required'"
          >
            <img slot="append" :src="'/static/cards/' + cardType + '.svg'" height="24px" v-if="cardType && cardType!=='unknown'" />
          </v-text-field>
        </v-flex>
        <v-flex xs6 sm2>
          <v-text-field
            label="Expiration"
            type="tel"
            v-model="fields.card_expiration"
            append-icon="fa-calendar"
            mask="## / ##"
            :rules="expirationRules"
            box
            hide-details
            required
            :error-messages="fields.card_expiration ? '' : 'Required'"
          ></v-text-field>
        </v-flex>
        <v-flex xs6 sm2>
          <v-text-field
            label="CVV"
            type="test"
            v-model="fields.card_cvv"
            :rules="cvvRules"
            :mask="cvvMask"
            box
            hide-details
            required
            :error-messages="fields.card_cvv ? '' : 'Required'"
          ></v-text-field>
        </v-flex>
      </v-layout>

      <v-layout row wrap v-if="paymentType === 'ach'">
        <v-flex xs12 sm4>
          <v-text-field
            label="Account Holder Name"
            v-model="fields.card_name"
            append-icon="fa-user"
            :rules="nameRules"
            box
            hide-details
            required
            :error-messages="fields.card_name ? '' : 'Required'"
          ></v-text-field>
        </v-flex>
        <v-flex xs6 sm4 pb-0>
          <v-text-field
            label="Routing Number"
            v-model="fields.ach_routing_number"
            append-icon="fa-exchange"
            :rules="routingRules"
            mask="#########"
            box
            hide-details
            required
            :error-messages="fields.ach_routing_number ? '' : 'Required'"
          ></v-text-field>
          <AchFindInfoModal></AchFindInfoModal>
        </v-flex>
        <v-flex xs6 sm4 pb-0>
          <v-text-field
            label="Account Number"
            v-model="fields.ach_account_number"
            append-icon="fa-institution"
            :rules="accountRules"
            box
            hide-details
            required
            :error-messages="fields.ach_account_number ? '' : 'Required'"
          ></v-text-field>
          <AchFindInfoModal></AchFindInfoModal>
        </v-flex>
        <v-flex xs6 sm4>
          <v-select
            label="Type"
            v-model="fields.ach_type"
            :items="achTypes"
            box
            hide-details
            required
            :error-messages="fields.ach_type ? '' : 'Required'"
          ></v-select>
        </v-flex>
        <v-flex xs6 sm4 pb-0 offset-sm4>
          <v-text-field
            label="Re-enter Account Number"
            v-model="fields.ach_account_number_verify"
            append-icon="fa-institution"
            :rules="accountVerifyRules"
            box
            hide-details
            required
            :error-messages="fields.ach_account_number_verify ? '' : 'Required'"
            onpaste="return false;"
            ondrop="return false;"
          ></v-text-field>
        </v-flex>
      </v-layout>

      <v-divider class="primary"></v-divider>

      <v-layout row wrap>
        <v-flex xs12 sm4>
          <v-text-field
            label="Address"
            v-model="fields.billing_address"
            append-icon="fa-home"
            box
            hide-details
            required
          ></v-text-field>
        </v-flex>
        <v-flex xs6 sm4>
          <v-text-field
            label="City"
            v-model="fields.billing_city"
            box
            hide-details
          ></v-text-field>
        </v-flex>
        <v-flex xs6 sm2>
          <StateField
            v-model="fields.billing_state"
            :textfield="fields.billing_country !== 'USA'"
          ></StateField>
        </v-flex>
        <v-flex xs6 sm2>
          <v-text-field
            label="Postal Code"
            v-model="fields.billing_postalcode"
            :rules="postalcodeRules"
            box
            hide-details
            :error-messages="fields.card_name ? '' : 'Required'"
            required
          ></v-text-field>
        </v-flex>
        <v-flex xs12 sm4>
          <v-text-field
            label="Phone"
            v-model="fields.billing_phone"
            mask="(###) ###-####"
            append-icon="fa-phone"
            box
            hide-details
          ></v-text-field>
        </v-flex>
        <v-flex xs12 sm4>
          <v-text-field
            label="Email"
            v-model="fields.billing_email"
            append-icon="fa-at"
            :rules="emailRules"
            box
            hide-details
          ></v-text-field>
        </v-flex>
        <v-flex xs6 sm4>
          <CountryField
            v-model="fields.billing_country"
            icon="fa-globe"
          ></CountryField>
        </v-flex>

      </v-layout>

      <v-divider class="primary"></v-divider>

      <v-layout row wrap>
        <v-flex xs6 sm4>
          <v-text-field
            label="Token Name"
            v-model="fields.token_name"
            maxlength="30"
            append-icon="fa-edit"
            box
            hide-details
          >
          </v-text-field>
        </v-flex>

        <!-- @TODO Add customerPO to billing record, backfill from previous transactions -->
        <v-flex xs6 sm4>
          <v-text-field
            label="Customer PO"
            v-model="fields.customer_po"
            append-icon="fa-file-text-o"
            box
            hide-details
            disabled
          >
            <div slot="append">
              <Lightbulb>
                <template v-slot:message>
                  <span>Used to relate transactions to your management software.</span>
                </template>
              </Lightbulb>
            </div>
          </v-text-field>
        </v-flex>

        <v-flex xs12>
          <v-textarea
            label="Notes (Internal Use Only will not print on customer receipt)"
            v-model="fields.notes"
            box
            hide-details
            height
            rows="1"
          ></v-textarea>
        </v-flex>
      </v-layout>

      <v-divider class="primary"></v-divider>

      <v-layout row wrap align-center fill-height>
          <v-flex xs12 sm4>
          </v-flex>

            <v-flex xs12 sm8>
              <v-layout row wrap justify-end>
                <v-btn color="primary" @click="validateBeforeProcessing" :disabled="!this.isFormValid || isProcessing ">tokenize</v-btn>
                <v-btn @click="reset">clear</v-btn>
              </v-layout>
            </v-flex>
      </v-layout>
    </v-form>

    <v-dialog v-model="resultModal" maxWidth="500px" @keydown.esc.prevent="closeResult()" @input="v => v || closeResult()" :transition="false">
      <v-toolbar dense>
        <v-toolbar-title><v-icon v-if="isResponseError">fa-exclamation-triangle</v-icon><v-icon v-if="!isResponseError">fa-check</v-icon> {{ isResponseError ? 'Error' : 'Success' }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon flat color="primary" @click="closeResult">
          <v-icon>close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card>
        <v-card-text class="text-xs-center">
          <h3 v-if="isResponseError">{{ responseMessage }}</h3>

          <h4 class="primary--text" v-if="isResponseError && responseMessageDetails">{{ responseMessageDetails }}</h4>

          <h3 v-if="!isResponseError">
            <span v-if="transactionDetails.Token">
              The {{ paymentType === 'card' ? 'card' : 'ACH account'}} <span class="title primary--text">{{ fields.card_number.slice(-4) }}</span> for <span class="title primary--text">{{ fields.card_name }}</span> has been tokenized.
              <div>Token ID: <span class="title primary--text">{{ transactionDetails.Token }}</span></div>
            </span>

            <div v-if="responseMessageDetails">{{ responseMessageDetails }}.</div>
          </h3>
        </v-card-text>
      </v-card>
    </v-dialog>

    <ConfirmModal
      v-model="showConfirmationModal"
      @confirmed="process"
      title="tokenization"
    ></ConfirmModal>

  </v-container>
</template>

<script>
import Bus from '@/bus'

import APIMixin from '@/mixins/api'
import UtilityMixin from '@/mixins/utility'
import ValidationMixin from '@/mixins/validation'

import AccountTerminalDebug from '@/components/terminal/components/AccountTerminalDebug.vue'
import AchFindInfoModal from '@/components/help/AchFindInfoDialog.vue'
import ConfirmModal from '@/components/ConfirmModal.vue'
import CountryField from '@/components/forms/CountryDropdown.vue'
import Lightbulb from '@/components/help/Lightbulb'
import StateField from '@/components/forms/StateDropdown.vue'
import TerminalSelect from '@/components/terminal/components/TerminalSelect.vue'

export default {
  mixins: [
    APIMixin,
    UtilityMixin,
    ValidationMixin
  ],

  components: {
    AccountTerminalDebug,
    AchFindInfoModal,
    ConfirmModal,
    CountryField,
    Lightbulb,
    StateField,
    TerminalSelect
  },

  props: {
    paymentType: String
  },

  data: function () {
    return {
      isFormValid: false,
      selectedTerminal: {},
      fields: this.defaultFields(),
      isProcessing: false,
      transactionDetails: {},
      responseMessage: '',
      responseMessageDetails: '',
      isResponseError: false,
      showConfirmationModal: false,
      receiptModal: false,
      resultModal: false,
      submitNonce: '',
      achTypes: [
        {value: 'CHECKING', text: 'Personal checking'},
        {value: 'SAVINGS', text: 'Personal savings'},
        {value: 'BUSINESSCK', text: 'Business checking'}
      ],
      achAuthOptions: [
        {value: 'none', text: 'None'},
        {value: 'onfile', text: 'Authorization on file'},
        {value: 'printsign', text: 'Print and sign now'},
        {value: 'email', text: 'Send authorization email'}
      ],
      // terminalRules: [
      //   (v) => !!v || 'Terminal is required'
      // ],
      nameRules: [
        (v) => !!v || 'Name is required'
      ],
      cardRules: [
        (v) => !!v || 'Card number is required',
        (v) => this.validateCardNumber(v) || 'Card number is invalid'
      ],
      cvvRules: [
        (v) => !!v || 'CVV is required'
      ],
      expirationRules: [
        (v) => !!v || 'Expiration is required'
      ],
      routingRules: [
        (v) => !!v || 'Routing number is required',
        (v) => v.length === 9 || 'Routing number must be nine digits',
        (v) => this.validateABA(v) || 'Routing number is invalid'
      ],
      accountRules: [
        (v) => !!v || 'Account number is required',
        (v) => (v.length < 18) || 'Account number must be less than 18 digits'
        // (v) => (v.toString() === this.fields.ach_account_number_verify) || 'Account number does not match verification'
      ],
      accountVerifyRules: [
        (v) => !!v || 'Account number verification is required',
        (v) => (v.length < 18) || 'Account number verification must be less than 18 digits',
        (v) => (v.toString() === this.fields.ach_account_number) || 'Account number verification does not match'
      ],
      postalcodeRules: [
        (v) => !!v || 'Postal code is required'
      ],
      emailRules: [
        (v) => !v || /.+@.+/.test(v) || 'Email must be valid'
      ]
    }
  },

  mounted () {
    this.reset()
  },

  computed: {
    cardMask () {
      if (this.cardType === 'amex') {
        return '#### ###### #####'
      } else if (this.cardType === 'diners') {
        return '#### ###### #####'
      } else if (this.cardType === 'discover') {
        return '#### #### #### ####'
      } else if (this.cardType === 'jcb') {
        return '#### #### #### ####'
      } else if (this.cardType === 'jcb15') {
        return '#### ###### #####'
      } else if (this.cardType === 'maestro') {
        return '#### #### #### ####'
      } else if (this.cardType === 'mastercard') {
        return '#### #### #### ####'
      } else if (this.cardType === 'unionpay') {
        return '#### #### #### ####'
      } else if (this.cardType === 'visa') {
        return '#### #### #### ####'
      }
      return '#### #### #### ####'
    },

    cvvMask () {
      if (this.cardType === 'amex' || this.cardType === 'diners' || this.cardType === 'jcb15') {
        return '####'
      }
      return '###'
    },

    cardType () {
      let amex = /^3[47]\d{0,13}/
      let diners = /^3(?:0([0-5]|9)|[689]\d?)\d{0,11}/
      let discover = /^(?:6011|65\d{0,2}|64[4-9]\d?)\d{0,12}/
      let jcb = /^(?:35\d{0,2})\d{0,12}/
      let jcb15 = /^(?:2131|1800)\d{0,11}/
      let maestro = /^(?:5[0678]\d{0,2}|6304|67\d{0,2})\d{0,12}/
      let mastercard = /^(5[1-5]\d{0,2}|22[2-9]\d{0,1}|2[3-7]\d{0,2})\d{0,12}/
      let unionpay = /^62\d{0,14}/
      let visa = /^4\d{0,15}/

      if (amex.test(this.fields.card_number)) {
        return 'amex'
      } else if (diners.test(this.fields.card_number)) {
        return 'diners'
      } else if (discover.test(this.fields.card_number)) {
        return 'discover'
      } else if (jcb.test(this.fields.card_number)) {
        return 'jcb'
      } else if (jcb15.test(this.fields.card_number)) {
        return 'jcb15'
      } else if (maestro.test(this.fields.card_number)) {
        return 'maestro'
      } else if (mastercard.test(this.fields.card_number)) {
        return 'mastercard'
      } else if (unionpay.test(this.fields.card_number)) {
        return 'unionpay'
      } else if (visa.test(this.fields.card_number)) {
        return 'visa'
      }
      return 'unknown'
    }
  },

  methods: {
    defaultFields () {
      return {
        account_id: 0,
        card_name: '',
        card_number: '',
        card_cvv: null,
        card_expiration: '',
        ach_routing_number: '',
        ach_account_number: '',
        ach_account_number_verify: '',
        ach_check_number: null,
        ach_type: 'CHECKING',
        ach_authorization: 'none',
        billing_address: '',
        billing_city: '',
        billing_state: '',
        billing_postalcode: '',
        billing_country: 'USA',
        billing_phone: null,
        billing_email: null,
        token_name: '',
        customer_po: null,
        notes: null
      }
    },

    async reset (opts) {
      this.selectedTerminal = {}
      this.$refs.terminal.reset()
      this.isProcessing = false
      this.responseMessage = ''
      this.responseMessageDetails = ''
      this.isResponseError = false
      this.transactionDetails = {}
      this.showConfirmationModal = false
      this.resultModal = false
      this.receiptModal = false
      this.$refs.form1.resetValidation()
      this.fields = this.defaultFields()
    },

    closeResult () {
      if (!this.isResponseError) {
        this.reset()
      } else {
        this.resultModal = false

        this.isProcessing = false
        this.responseMessage = ''
        this.responseMessageDetails = ''
        this.isResponseError = false
      }
    },

    async validateBeforeProcessing () {
      if (!this.$refs.form1.validate()) {
        let errors = []
        this.$refs.form1.inputs.forEach(e => {
          if (e.hasError) {
            errors.push(e.errorBucket[0])
          }
        })

        let message = 'Validation Error'

        if (errors.length > 1) {
          message = 'Validation Errors'
        }

        Bus.$emit('openModal', {
          status: 'error',
          message: message,
          messageDetails: errors
        })

        return false
      } else {
        this.showConfirmModal()
      }
    },

    showConfirmModal () {
      this.showConfirmationModal = true
    },

    async process () {
      try {
        let options = { ...this.fields }

        if (!this.selectedTerminal.terminal_id) {
          Bus.$emit('openModal', {
            status: 'error',
            message: 'Please select a terminal'
          })

          return false
        }
        options.terminal_id = this.selectedTerminal.terminal_id

        this.isProcessing = true
        Bus.$emit('openWaitModal')

        options.nonce = this.submitNonce

        if (this.paymentType === 'card') {
          delete options.ach_routing_number
          delete options.ach_account_number
          delete options.ach_account_number_verify
          delete options.ach_check_number
          delete options.ach_type
          delete options.ach_authorization

          options.card_month = options.card_expiration.slice(0, 2)
          options.card_year = options.card_expiration.slice(-2)

          options.request_type = 'sale'
        } else if (this.paymentType === 'ach') {
          // if (options.ach_authorization === 'email' && !options.billing_email) {
          //   Bus.$emit('openModal', {
          //     status: 'error',
          //     message: 'Email required for ACH authorization'
          //   })
          //   return false
          // }

          delete options.card_number
          delete options.card_cvv
          delete options.card_expiration
          delete options.fee

          options.request_type = 'ach'
        } else {
          Bus.$emit('openModal', {
            status: 'error',
            message: 'Cannot determine payment type'
          })
          return false
        }

        let result = await this.api({
          method: 'post',
          url: `/terminal/tokenize`,
          data: options
        })

        // process the refund
        if (result.data.response.ResponseText === 'TOKENIZED') {
          this.transactionDetails = result.data.response
          this.responseMessage = 'Card has been tokenized'
        } else {
          let error = result.data.response.ResponseText
          if (result.data.response.ErrMsg) {
            error += ': ' + result.data.response.ErrMsg
          }
          this.transactionDetails = {}
          this.isResponseError = true
          this.responseMessage = error
          this.responseMessageDetails = ''
          this.isProcessing = false

          if (error === 'DECLINED') {
            this.responseMessageDetails = 'Please verify card data including the expiration and CVV'
          }

          if (result.data.response.Transaction && result.data.response.Transaction.hasOwnProperty('error_messages') && result.data.response.Transaction.error_messages) {
            let messages = JSON.parse(result.data.response.Transaction.error_messages)

            // show detailed error as long as it isn't the generic 'do not honor'
            if (messages.length > 0 && messages[0] !== 'TRANS DENIED DO NOT HONOR' && messages[0] !== error) {
              this.responseMessageDetails = messages.join()
            }
          }

          this.submitNonce = this.generateId(36)
        }

        this.resultModal = true
        this.panel = [true]
      } catch (err) {
        console.log('ERROR')
        console.log(err)

        this.submitNonce = this.generateId(36)
        Bus.$emit('openModal', {
          status: 'error',
          message: err
        })
      } finally {
        this.isProcessing = false
        Bus.$emit('closeWaitModal')
      }
    }

  }
}
</script>



// WEBPACK FOOTER //
// src/components/terminal/Tokenize.vue